<template>
  <Sheet :open="true" @update:open="emit('close')">
    <SheetContent class="gap-0 pb-2">
      <SheetHeader padded>
        <SheetTitle size="lg">Schedule Meeting with {{ contact.name }}</SheetTitle>
      </SheetHeader>

      <Separator />

      <SheetBody padded class="flex-1 min-h-0 overflow-y-auto space-y-6 py-4">
        <div v-if="!isEditingCandidateOutreach">
          <Label class="font-medium">Meeting Type</Label>
          <Select v-model="meetingType" :disabled="schedulingMode !== 'none'">
            <SelectTrigger class="mt-2">
              <SelectValue placeholder="Select meeting type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="phone_screen">Phone Screen</SelectItem>
              <SelectItem value="interview">Interview</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div v-if="!isEditingCandidateOutreach && showAutoUpdateStage" class="flex items-center gap-2">
          <Checkbox v-model="autoUpdateStage" />
          <span class="text-sm">Automatically update stage to</span>
          <Badge variant="purple">{{ RailsUtility.convertFromCasedEnumFormat(meetingType) }}</Badge>
        </div>

        <ScheduleInterviewInput :input="scheduleInput" />
      </SheetBody>

      <SheetFooter bordered class="flex-row justify-end gap-3">
        <template v-if="showSchedulingModeBack">
          <Button variant="ghost" class="mr-auto" @click="schedulingMode = 'none'">Back</Button>
        </template>
        <template v-if="!isEditingCandidateOutreach">
          <Button variant="outline" @click="emit('close')">Cancel</Button>
          <Button :disabled="isSubmitting || schedulingMode === 'none'" @click="handleSchedule">
            <div v-if="isSubmitting" class="spinner" />
            Schedule
          </Button>
        </template>
        <template v-if="isEditingCandidateOutreach">
          <Button @click="navigateToCandidateReview">Continue</Button>
        </template>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>

<script setup lang="ts">
import { ref, computed, toRef } from 'vue';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import ScheduleInterviewInput from '@/components/shared/ScheduleInterviewInput/ScheduleInterviewInput.vue';
import { useScheduleInterviewInput } from '@/components/shared/ScheduleInterviewInput/useScheduleInterviewInput';
import { useMoveContactStage } from '../ContactStageActions/useMoveContactStage';
import type { ContactEventType } from '@/api/entity_types/ContactEvent';
import type { ContactStage } from '@/api/entity_types/Contact';
import type { ContactDetail } from '../ContactDetail';
import RailsUtility from '@/utilities/rails';
import { toast } from 'vue-sonner';

const props = defineProps<{
  contact: Pick<
    ContactDetail,
    'id' | 'name' | 'stage' | 'firstName' | 'lastName' | 'email' | 'phone' | 'canReceiveSms'
  >;
}>();

const emit = defineEmits<{
  close: [];
  scheduled: [];
}>();

const contactRef = computed(() => ({ id: props.contact.id, stage: props.contact.stage }));
const moveContactStage = useMoveContactStage(contactRef);

const defaultMeetingType: ContactEventType =
  moveContactStage.nextStage.value === 'Phone Screen' ? 'phone_screen' : 'interview';
const meetingType = ref<ContactEventType>(defaultMeetingType);
const showAutoUpdateStage = computed(() => props.contact.stage === 'Applied' || props.contact.stage === 'Phone Screen');
const autoUpdateStage = ref(false);
const isSubmitting = ref(false);

const scheduleInput = useScheduleInterviewInput({
  contact: toRef(props, 'contact'),
  event_type: meetingType,
});

const { schedulingMode, showSchedulingModeBack, isEditingCandidateOutreach, navigateToCandidateReview } = scheduleInput;

const handleSchedule = async () => {
  if (isSubmitting.value) return;
  if (!scheduleInput.validate()) return;

  isSubmitting.value = true;
  try {
    if (autoUpdateStage.value) {
      const stage = RailsUtility.convertFromCasedEnumFormat(meetingType.value) as ContactStage;
      await moveContactStage.updateContactStage(stage);
    }
    await scheduleInput.submit();
    if (schedulingMode.value === 'admin_schedule') {
      toast.success('Meeting scheduled', { description: 'The meeting was successfully scheduled.' });
    } else {
      toast.success('Candidate notified', { description: 'The candidate has been sent scheduling instructions.' });
    }
    emit('scheduled');
  } finally {
    isSubmitting.value = false;
  }
};
</script>
